/* eslint-disable react/no-unescaped-entities */
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useT } from "@/lib/i18n";

export default function Pricing() {
  const t = useT();
  const scrollContact = () =>
    document
      .getElementById("contact")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });

  const plans = [
    {
      ...t.pricing.plans.essential,
      key: "essential",
      unit: t.pricing.unit,
      highlighted: false,
      testId: "pricing-essential-btn",
      cardTestId: "pricing-card-essential",
    },
    {
      ...t.pricing.plans.pro,
      key: "pro",
      unit: t.pricing.unit,
      badge: t.pricing.popular,
      highlighted: true,
      testId: "pricing-pro-btn",
      cardTestId: "pricing-card-pro",
    },
    {
      ...t.pricing.plans.domain,
      key: "domain",
      price: t.pricing.on_quote,
      unit: "",
      highlighted: false,
      testId: "pricing-domain-btn",
      cardTestId: "pricing-card-domain",
    },
  ];

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="py-24 md:py-32 bg-[#F0F0EB] relative"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-2xl mb-16">
          <div className="text-sm font-bold uppercase tracking-[0.2em] text-[#E05A3D]">
            {t.pricing.overline}
          </div>
          <h2 className="mt-4 font-display text-4xl md:text-5xl font-black tracking-tight text-[#1C3A27]">
            {t.pricing.headline}
          </h2>
          <p className="mt-6 text-lg text-[#5C6B5C] leading-relaxed">
            {t.pricing.description}
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 lg:gap-7 items-stretch">
          {plans.map((p) => (
            <div
              key={p.key}
              data-testid={p.cardTestId}
              className={`relative rounded-[1.75rem] p-8 md:p-10 flex flex-col border transition-all duration-300 ${
                p.highlighted
                  ? "bg-[#1C3A27] text-white border-[#1C3A27] lg:scale-[1.04] shadow-2xl shadow-[#1C3A27]/15 z-10"
                  : "bg-white border-[#E2E4DF] hover:-translate-y-1 hover:shadow-md"
              }`}
            >
              {p.badge && (
                <div
                  data-testid="pricing-badge-popular"
                  className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full bg-[#E05A3D] text-white text-xs font-bold uppercase tracking-wider whitespace-nowrap"
                >
                  {p.badge}
                </div>
              )}

              <div>
                <h3
                  className={`font-display text-2xl md:text-3xl font-bold ${
                    p.highlighted ? "text-white" : "text-[#1C3A27]"
                  }`}
                >
                  {p.name}
                </h3>
                <p
                  className={`mt-2 text-base ${
                    p.highlighted ? "text-white/70" : "text-[#5C6B5C]"
                  }`}
                >
                  {p.description}
                </p>
              </div>

              <div className="mt-8 flex items-end gap-1.5">
                <span
                  className={`font-display font-black text-5xl md:text-6xl ${
                    p.highlighted ? "text-white" : "text-[#1C3A27]"
                  }`}
                >
                  {p.price}
                </span>
                {p.unit && (
                  <span
                    className={`pb-2 text-base ${
                      p.highlighted ? "text-white/70" : "text-[#5C6B5C]"
                    }`}
                  >
                    {p.unit}
                  </span>
                )}
              </div>

              <ul className="mt-8 space-y-3.5 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <span
                      className={`mt-0.5 w-5 h-5 rounded-full grid place-items-center shrink-0 ${
                        p.highlighted
                          ? "bg-[#E05A3D] text-white"
                          : "bg-[#1C3A27] text-white"
                      }`}
                    >
                      <Check size={12} strokeWidth={3} />
                    </span>
                    <span
                      className={`${
                        p.highlighted ? "text-white/90" : "text-[#1C3A27]"
                      }`}
                    >
                      {f}
                    </span>
                  </li>
                ))}
              </ul>

              {p.note && (
                <p
                  data-testid={`pricing-note-${p.key}`}
                  className={`mt-5 text-xs leading-relaxed border-t pt-4 ${
                    p.highlighted
                      ? "text-white/70 border-white/20"
                      : "text-[#5C6B5C] border-[#E2E4DF]"
                  }`}
                >
                  + {p.note}
                </p>
              )}

              <Button
                data-testid={p.testId}
                onClick={scrollContact}
                className={`mt-9 h-12 rounded-2xl font-semibold text-base ${
                  p.highlighted
                    ? "bg-[#E05A3D] hover:bg-[#C84B31] text-white"
                    : "bg-[#1C3A27] hover:bg-[#142A1C] text-white"
                }`}
              >
                {p.cta}
              </Button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
